import { useSyncExternalStore } from "react";

// A single refcounted interval feeds every subscriber a `now` (floored to the
// second) via useSyncExternalStore, so any number of concurrent running pills
// share one interval rather than each owning a drifting one, React coalesces
// their per-second updates, and only leaf nodes that read the clock re-render.

function flooredNow(): number {
  return Math.floor(Date.now() / 1000) * 1000;
}
let intervalId: ReturnType<typeof setInterval> | null = null;
let currentNow = flooredNow();
const listeners = new Set<() => void>();
function tick(): void {
  const next = flooredNow();
  // Skip when the second hasn't advanced: a stable snapshot keeps
  // useSyncExternalStore from re-rendering subscribers needlessly.
  if (next === currentNow) return;
  currentNow = next;
  listeners.forEach(listener => listener());
}
function startIfNeeded(): void {
  if (intervalId !== null) return;
  // Re-seed so the first reader after an idle period sees a fresh second.
  currentNow = flooredNow();
  intervalId = setInterval(tick, 1000);
}
function stopIfIdle(): void {
  if (listeners.size > 0 || intervalId === null) return;
  clearInterval(intervalId);
  intervalId = null;
}
function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  startIfNeeded();
  return () => {
    listeners.delete(listener);
    stopIfIdle();
  };
}

// No-op subscription for disabled callers: the hook must run unconditionally
// (rules of hooks) but a non-running pill never joins the interval.
function subscribeDisabled(): () => void {
  return () => {};
}
function getSnapshot(): number {
  return currentNow;
}

// SSR: a stable value avoids a hydration mismatch; the live label resolves on
// the client once the interval attaches.
function getServerSnapshot(): number {
  return 0;
}

/**
 * Subscribe to the shared 1s clock. Returns `now` in ms (floored to the
 * second). When `enabled` is false the hook returns the current snapshot
 * without starting or joining the interval.
 */
export function useNowTick(enabled) {
  return useSyncExternalStore(enabled ? subscribe : subscribeDisabled, getSnapshot, getServerSnapshot);
}

/** Test-only: whether the shared interval is currently running. */
export function __isTickerRunning(): boolean {
  return intervalId !== null;
}

/** Test-only: number of active subscribers. */
export function __subscriberCount(): number {
  return listeners.size;
}